import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Mic, Music as MusicIcon, PenTool, Palette, Camera, Globe, Shirt,
  ArrowRight, Mail, MapPin, Sparkles, Quote,
} from "lucide-react";
import { api, imgUrl } from "../lib/api";
import Seo from "../components/Seo";

// Icon keys the admin can pick from for service cards
const ICON_MAP = {
  music: MusicIcon,
  pen: PenTool,
  palette: Palette,
  camera: Camera,
  globe: Globe,
  shirt: Shirt,
  mic: Mic,
  sparkles: Sparkles,
};

export default function About() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/about/config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  if (!cfg) {
    return (
      <div className="bg-[#0A0A0A] text-white min-h-screen flex items-center justify-center" data-testid="about-page">
        <div className="font-mono text-xs text-white/40 tracking-widest">LOADING…</div>
      </div>
    );
  }

  return (
    <div className="relative bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="about-page">
      <Seo
        title="About SyndaFam | Independent Canadian Multimedia Collective"
        description="The SyndaFam story — an independent Canadian multimedia collective from Toronto making hip-hop music, cinematic music videos, digital art and original entertainment."
        path="/about"
      />
      {/* Living aurora + drifting orbs */}
      <div className="fixed inset-0 -z-10 bg-aurora pointer-events-none opacity-70" aria-hidden="true" />
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true">
        <div className="orb orb-red orb-live"   style={{ width: 620, height: 620, top: "-160px", left: "-180px" }} />
        <div className="orb orb-amber orb-live" style={{ width: 520, height: 520, bottom: "-160px", right: "-140px", animationDelay: "-6s" }} />
        <div className="orb orb-cyan orb-live"  style={{ width: 360, height: 360, top: "40%", left: "40%", opacity: 0.18, animationDelay: "-3s" }} />
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden grain-overlay">
        <div
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.85) 60%, #0A0A0A 100%), url(${imgUrl(cfg.hero_bg_image)})`,
            backgroundSize: "cover",
            backgroundPosition: "center 25%",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-4 text-glow-red reveal reveal-1">{cfg.hero_eyebrow}</div>
            <h1 className="font-display leading-[0.85] tracking-tighter reveal reveal-2" style={{ fontSize: "clamp(3rem, 10vw, 9rem)" }}>
              {cfg.hero_line_1}<br />
              <span className="text-[#E63946] text-glow-red">{cfg.hero_line_2}</span><br />
              <span className="text-[#FCA311] font-graffiti normal-case text-[70%] text-glow-amber">{cfg.hero_line_3}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed">
              {cfg.hero_subtitle}
            </p>
          </div>
          <div className="lg:col-span-4 hidden lg:block" data-testid="about-hero-cover">
            <div className="relative aspect-[3/4] overflow-hidden rounded-3xl glass-card-strong ai-border float-card reveal reveal-3">
              <img src={imgUrl(cfg.hero_cover_image)} alt="Synda Fam — cover" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 pb-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-white/10 pt-8" data-testid="about-stats">
            {(cfg.stats || []).map((s, i) => (
              <div key={i}>
                <div className="font-display text-5xl sm:text-6xl">{s.value}</div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-white/40 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORIGIN */}
      <section className="py-20" data-testid="about-origin">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 lg:sticky lg:top-24 self-start space-y-8">
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-3">{cfg.origin_eyebrow}</div>
              <h2 className="font-display text-5xl sm:text-6xl leading-[0.9]">
                {cfg.origin_title_1}<br />
                <span className="text-[#E63946]">{cfg.origin_title_2}</span>
              </h2>
            </div>
            <div className="relative aspect-[4/5] overflow-hidden rounded-3xl glass-card-strong float-card" data-testid="origin-photo">
              <img src={imgUrl(cfg.origin_photo)} alt="Synda Fam origin" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
                <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">{cfg.origin_photo_tag}</div>
                <div className="font-display text-xl text-white mt-1">{cfg.origin_photo_caption}</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-5 text-white/75 text-lg leading-relaxed">
            {(cfg.origin_paragraphs || []).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* HISTORY */}
      <section className="py-20" data-testid="about-history">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
            <div className="lg:col-span-8">
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-3">{cfg.history_eyebrow}</div>
              <h2 className="font-display text-5xl sm:text-6xl leading-[0.9]">
                {cfg.history_title_1}<br />
                <span className="font-graffiti text-[#FCA311] normal-case text-[60%]">{cfg.history_title_2}</span>
              </h2>
            </div>
            <div className="lg:col-span-4 hidden lg:block" data-testid="history-photo">
              <div className="relative aspect-[3/4] overflow-hidden border-2 border-[#E63946]">
                <img src={imgUrl(cfg.history_photo)} alt="History" className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3">
                  <div className="font-mono text-[9px] tracking-[0.3em] text-[#FCA311]">{cfg.history_photo_tag}</div>
                  <div className="font-display text-lg text-white">{cfg.history_photo_caption}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 space-y-5 text-white/75 text-lg leading-relaxed">
              {(cfg.history_paragraphs || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <div className="lg:col-span-5" data-testid="about-timeline">
              <div className="font-mono text-[10px] tracking-[0.3em] text-white/40 mb-4">// THE TIMELINE</div>
              <ol className="relative border-l-2 border-[#E63946]/40 ml-3 space-y-7">
                {(cfg.timeline || []).map((t, i) => (
                  <li key={i} className="pl-6 relative" data-testid={`timeline-${i}`}>
                    <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-[#E63946] shadow-[0_0_18px_#E63946]" />
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">{t.year}</div>
                    <div className="font-display text-xl mt-1">{t.title}</div>
                    <p className="text-white/60 text-sm mt-2 leading-relaxed">{t.body}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="relative py-20 overflow-hidden" data-testid="about-vision">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.7) 0%, rgba(10,10,10,0.9) 100%), url(${imgUrl(cfg.vision_bg_image)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-3">{cfg.vision_eyebrow}</div>
            <h2 className="font-display text-5xl sm:text-6xl leading-[0.9]">
              {cfg.vision_title_1}<br /><span className="text-[#E63946]">{cfg.vision_title_2}</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-white/75 text-lg leading-relaxed">{cfg.vision_body}</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="vision-pillars">
              {(cfg.vision_pillars || []).map((p) => (
                <div
                  key={p}
                  className="border border-white/15 hover:border-[#E63946] transition-colors px-4 py-3 font-mono text-xs tracking-[0.2em] text-white/80 flex items-center gap-2"
                >
                  <Sparkles className="w-3.5 h-3.5 text-[#FCA311]" />
                  {p.toUpperCase()}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-20" data-testid="about-mission">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-3">{cfg.mission_eyebrow}</div>
          <h2 className="font-display text-5xl sm:text-6xl leading-[0.9] mb-10">
            {cfg.mission_title_1}<br />
            <span className="font-graffiti text-[#FCA311] normal-case text-[60%]">{cfg.mission_title_2}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="mission-cards">
            {(cfg.missions || []).map((m, i) => (
              <div
                key={i}
                className="glass-card-strong ai-border rounded-3xl p-8 float-card"
                data-testid={`mission-card-${i}`}
              >
                <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311]">{m.tag}</div>
                <h3 className="font-display text-4xl mt-2">{m.title}</h3>
                <p className="text-white/70 mt-4 leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20" data-testid="about-services">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-3">{cfg.services_eyebrow}</div>
              <h2 className="font-display text-5xl sm:text-6xl leading-[0.9]">
                {cfg.services_title_1} <span className="text-[#E63946]">{cfg.services_title_2}</span>
              </h2>
            </div>
            <Link
              to="/connect"
              className="font-mono text-xs tracking-widest text-white/60 hover:text-[#E63946] inline-flex items-center gap-2"
              data-testid="about-services-cta"
            >
              BOOK THE FAM <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
            {(cfg.services || []).map((s, i) => {
              const Icon = ICON_MAP[s.icon] || MusicIcon;
              return (
                <div
                  key={i}
                  className="glass-card-strong rounded-2xl float-card group overflow-hidden"
                  data-testid={`service-${i}`}
                >
                  <div className="aspect-[16/10] bg-black overflow-hidden relative">
                    <img src={imgUrl(s.image)} alt={s.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#151515] via-transparent to-transparent" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 border border-white/15 group-hover:border-[#FCA311] flex items-center justify-center transition-colors flex-shrink-0">
                        <Icon className="w-4 h-4 text-[#FCA311]" />
                      </div>
                      <div className="font-display text-2xl">{s.title}</div>
                    </div>
                    <p className="text-white/60 text-sm mt-3 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MESSAGE FROM THE TEAM */}
      <section className="py-20" data-testid="about-message">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-10 text-center">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-3">{cfg.message_eyebrow}</div>
          <Quote className="w-12 h-12 text-[#FCA311] mx-auto opacity-70" />
          <p className="font-display text-2xl sm:text-4xl leading-snug mt-6 whitespace-pre-wrap">
            &ldquo;{cfg.message_body}&rdquo;
          </p>
          <div className="font-mono text-[10px] tracking-[0.4em] text-white/50 mt-8">{cfg.message_signature}</div>
        </div>
      </section>

      {/* MEET THE TEAM */}
      <section className="py-20" data-testid="about-team-cta">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-8 mb-12">
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-3">{cfg.team_eyebrow}</div>
              <h2 className="font-display text-5xl sm:text-6xl leading-[0.9] flex items-center gap-3 flex-wrap">
                <Mic className="w-12 h-12 text-[#E63946]" /> {cfg.team_title}
              </h2>
              <p className="text-white/60 mt-4 max-w-xl">{cfg.team_subtitle}</p>
            </div>
            <Link
              to="/artists"
              className="font-display text-xl rounded-2xl px-8 py-5 bg-[#E63946] hover:bg-[#C92A36] text-white inline-flex items-center gap-3 transition-colors shadow-[0_0_32px_rgba(230,57,70,0.55)]"
              data-testid="about-roster-btn"
            >
              {cfg.team_cta_label} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5" data-testid="team-grid">
            {(cfg.team || []).map((m, i) => (
              <div key={i} className="group" data-testid={`team-member-${i}`}>
                <div className="relative aspect-[3/4] overflow-hidden rounded-2xl glass-card-strong float-card">
                  <img src={imgUrl(m.photo)} alt={m.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="font-display text-lg leading-tight">{m.name}</div>
                    <div className="font-mono text-[9px] tracking-[0.2em] text-[#FCA311] mt-1">{(m.role || "").toUpperCase()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="relative py-20 overflow-hidden" data-testid="about-contact">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="relative overflow-hidden rounded-3xl glass-card-strong ai-border float-card p-8 sm:p-12">
            <div
              className="absolute inset-0 opacity-15 pointer-events-none"
              style={{
                backgroundImage: `url(${imgUrl(cfg.contact_bg_image)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div className="orb orb-red pointer-events-none" style={{ width: 380, height: 380, top: -100, right: -60, opacity: 0.35 }} />
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-3 text-glow-red">{cfg.contact_eyebrow}</div>
                <h3 className="font-display text-5xl sm:text-6xl leading-none text-white">{cfg.contact_title}</h3>
                <p className="text-white/70 mt-4 max-w-md">{cfg.contact_body}</p>
              </div>
              <div className="space-y-4 text-white/85">
                <div className="flex items-center gap-3" data-testid="contact-website">
                  <Globe className="w-5 h-5 text-[#FCA311]" />
                  <span className="font-mono text-sm">{cfg.contact_website}</span>
                </div>
                <div className="flex items-center gap-3" data-testid="contact-email">
                  <Mail className="w-5 h-5 text-[#FCA311]" />
                  <a href={`mailto:${cfg.contact_email}`} className="font-mono text-sm hover:text-[#FCA311]">
                    {cfg.contact_email}
                  </a>
                </div>
                <div className="flex items-center gap-3" data-testid="contact-address">
                  <MapPin className="w-5 h-5 text-[#FCA311]" />
                  <span className="font-mono text-sm">{cfg.contact_address}</span>
                </div>
                <Link
                  to="/connect"
                  className="inline-flex items-center gap-3 mt-3 font-display text-lg rounded-2xl px-6 py-4 bg-[#E63946] text-white hover:bg-[#C92A36] transition-colors shadow-[0_0_28px_rgba(230,57,70,0.55)]"
              data-testid="about-connect-btn"
            >
              <Mail className="w-5 h-5" /> CONNECT WITH US
            </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
